import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import {
    type ThresholdRange,
    useGetBehaviorOverview,
    useGetRespiration,
    useGetSwimSpeed,
    useGetTilt,
} from '@/domain/behavior';
import { isValidCageId } from '@/domain/cages';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';

import BehaviorDashboardView from './BehaviorDashboardView';
import BehaviorExportButton from './components/BehaviorExportButton';

export const SWIM_SPEED_THRESHOLD_DEFAULT: ThresholdRange = {
    min: 0.5,
    max: 1.5,
    optimal: 1,
};

export const TILT_THRESHOLD_DEFAULT: ThresholdRange = {
    min: 8,
    max: 12,
    optimal: 10,
};

export default function BehaviorDashboardContainer() {
    const { t } = useTranslation();
    const { cageId, bioscope } = useCage();
    const cageName = useCageName();
    const bioscopeId = bioscope?.id;
    const { selectedFishGroupId } = useCageFilter();

    const {
        dateFrom: tiltDateFrom,
        dateTo: tiltDateTo,
        onDateChange: onTiltDateChange,
    } = useDateRangePicker({
        key: 'tilt',
        defaultPresetId: 'last7days',
    });

    const {
        dateFrom: respirationDateFrom,
        dateTo: respirationDateTo,
        selectedPresetId: respirationPresetId,
        onDateChange: onRespirationDateChange,
    } = useDateRangePicker({
        key: 'respiration',
        defaultPresetId: 'last7days',
    });

    const {
        dateFrom: swimSpeedDateFrom,
        dateTo: swimSpeedDateTo,
        selectedPresetId: swimSpeedPresetId,
        onDateChange: onSwimSpeedDateChange,
    } = useDateRangePicker({
        key: 'swimSpeed',
        defaultPresetId: 'last7days',
    });

    // Memoize params objects to prevent unnecessary re-renders and maintain stable queryKeys
    const swimSpeedParams = useMemo(
        () => ({
            from: swimSpeedDateFrom,
            to: swimSpeedDateTo,
            showHourly: swimSpeedPresetId === '24h',
            fishGroupId: selectedFishGroupId,
        }),
        [swimSpeedDateFrom, swimSpeedDateTo, swimSpeedPresetId, selectedFishGroupId]
    );

    const respirationParams = useMemo(
        () => ({
            from: respirationDateFrom,
            to: respirationDateTo,
            showHourly: respirationPresetId === '24h',
            fishGroupId: selectedFishGroupId,
        }),
        [respirationDateFrom, respirationDateTo, respirationPresetId, selectedFishGroupId]
    );

    const tiltParams = useMemo(
        () => ({
            from: tiltDateFrom,
            to: tiltDateTo,
            fishGroupId: selectedFishGroupId,
        }),
        [tiltDateFrom, tiltDateTo, selectedFishGroupId]
    );

    const overviewQuery = useGetBehaviorOverview(
        cageId,
        { fishGroupId: selectedFishGroupId },
        { enabled: isValidCageId(cageId) }
    );
    const {
        data: overviewData,
        isLoading: overviewIsLoading,
        error: overviewError,
    } = overviewQuery;
    const swimSpeedQuery = useGetSwimSpeed(cageId, swimSpeedParams, {
        enabled: isValidCageId(cageId),
    });
    const {
        data: swimSpeedData,
        isLoading: swimSpeedIsLoading,
        error: swimSpeedError,
    } = swimSpeedQuery;
    const respirationQuery = useGetRespiration(cageId, respirationParams, {
        enabled: isValidCageId(cageId),
    });
    const {
        data: respirationData,
        isLoading: respirationIsLoading,
        error: respirationError,
    } = respirationQuery;
    const tiltQuery = useGetTilt(cageId, tiltParams, {
        enabled: isValidCageId(cageId),
    });
    const { data: tiltData, isLoading: tiltIsLoading, error: tiltError } = tiltQuery;

    const swimSpeedDateRange: DateRange = useMemo(
        () => ({
            dateFrom: swimSpeedDateFrom,
            dateTo: swimSpeedDateTo,
        }),
        [swimSpeedDateFrom, swimSpeedDateTo]
    );

    const respirationDateRange: DateRange = useMemo(
        () => ({
            dateFrom: respirationDateFrom,
            dateTo: respirationDateTo,
        }),
        [respirationDateFrom, respirationDateTo]
    );

    const tiltDateRange: DateRange = useMemo(
        () => ({
            dateFrom: tiltDateFrom,
            dateTo: tiltDateTo,
        }),
        [tiltDateFrom, tiltDateTo]
    );

    return (
        <PageLayout
            title={cageName}
            subtitle={t('behaviorModule.common.behavior')}
            actions={<BehaviorExportButton cageId={cageId} disabled={!isValidCageId(cageId)} />}>
            <BehaviorDashboardView
                overview={overviewData}
                overviewIsLoading={overviewIsLoading}
                overviewError={overviewError}
                overviewRetry={retryFailedQueries(overviewQuery)}
                swimSpeedDateRange={swimSpeedDateRange}
                onSwimSpeedDateChange={onSwimSpeedDateChange}
                swimSpeedData={swimSpeedData}
                swimSpeedIsLoading={swimSpeedIsLoading}
                swimSpeedError={swimSpeedError}
                swimSpeedRetry={retryFailedQueries(swimSpeedQuery)}
                respirationDateRange={respirationDateRange}
                onRespirationDateChange={onRespirationDateChange}
                respirationData={respirationData}
                respirationIsLoading={respirationIsLoading}
                respirationError={respirationError}
                respirationRetry={retryFailedQueries(respirationQuery)}
                tiltDateRange={tiltDateRange}
                onTiltDateChange={onTiltDateChange}
                tiltData={tiltData}
                tiltIsLoading={tiltIsLoading}
                tiltError={tiltError}
                tiltRetry={retryFailedQueries(tiltQuery)}
                cageName={cageName}
                cageId={cageId}
                bioscopeId={bioscopeId}
            />
        </PageLayout>
    );
}
